import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import ActionBar from '../../../../shared/components/v3/ActionBar/ActionBar';
import EmptyCard from '../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import ErrorCard from '../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import StyledTable from '../../../../shared/components/v3/Table/StyledTable/StyledTable';
import TablePaginationV3 from '../../../../shared/components/v3/TablePagination/TablePagination';
import { DEFAULT_PAGINATOR_OPTIONS_V3, DEFAULT_PAGINATOR_STATE_V3 } from '../../../../shared/constants/constants';
import { SECTION_COLLABORATORS } from '../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { COLLABORATORS_TO_ADD_TABLE_COLUMNS } from '../../../../shared/handlers/tableColumns';
import { useIsMounted } from '../../../../shared/hooks/useIsMounted';
import { IToAddMembersData } from '../../../../shared/interfaces/collaborators';
import { IMembersToAddResponse } from '../../../../shared/interfaces/responseInterface';
import { ISelectedMap } from '../../../../shared/interfaces/tables';
import Holding from '../../../../shared/services/api/holdings/holdings';
import { TRANSLATION_COLLABORATORS } from '../../../../shared/translations/collaborators';
import { TRANSLATIONS_GLOBAL } from '../../../../shared/translations/commons';
import { sortArray } from '../../../../shared/utils/utils';
import { isEmptyObject } from '../../../../shared/utils/validation/validation';
import TableFilter from '../TableFilter/TableFilter';
import CollaboratorsAssignModal from './CollaboratorsToAddModal/CollaboratorsAssignModal/CollaboratorsAssignModal';
import CollaboratorsDismissModal from './CollaboratorsToAddModal/CollaboratorsDismissModal/CollaboratorsDissmisModal';

import './CollaboratorsToAddTable.scss';

const CollaboratorToAddTable = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [collaboratorsToAdd, setCollaboratorsToAdd] = useState<IToAddMembersData[] | undefined>(undefined);
  const [integrationName, setIntegrationName] = useState<string>('');
  const [lastSyncAt, setLastSyncAt] = useState<string[]>([]);
  const [paginatorState, setPaginatorState] = useState<number>(DEFAULT_PAGINATOR_STATE_V3);
  const [actualPage, setActualPage] = useState<number>(0);
  const [currentPageData, setCurrentPageData] = useState<IToAddMembersData[]>([]);
  const [selectedMap, setSelectedMap] = useState<ISelectedMap>({});
  const [allSelected, setAllSelected] = useState(false);
  const [totalSelected, setTotalSelected] = useState<number>(0);
  const [error, setError] = useState(false);
  const [addCollaboratorModal, setAddCollaboratorModal] = useState<boolean>(false);
  const [deleteCollaboratorModal, setDeleteCollaboratorModal] = useState<boolean>(false);
  const [emptySearch, setEmptySearch] = useState<boolean>(false);
  const { holding } = useContext(CompanyContext);
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const actionBarText = useMemo(
    () => t(TRANSLATION_COLLABORATORS.table.actionBar, { count: totalSelected, items: totalSelected }),
    [t, totalSelected]
  );

  const onSearch = async (searchValue?: string) => {
    setLoading(true);
    try {
      let response: IMembersToAddResponse;
      setError(false);
      if (holding) {
        response = await Holding.getMembersToAdd(holding.id, searchValue?.toLowerCase());
        setEmptySearch(!response.pendingMembers.length);
        if (isMounted) {
          const sorted = [...response.pendingMembers].sort((a, b) => a.email.localeCompare(b.email));
          setCollaboratorsToAdd(sorted);
          setLastSyncAt(response.integrationLastSync ? response.integrationLastSync.split(' ') : []);
          setIntegrationName(response.integrationName ?? '');
        }
      }
    } catch {
      setError(true);
    }
  };

  const getTotalCollaboratorsToAdd = () => {
    return collaboratorsToAdd ? collaboratorsToAdd.length : 0;
  };

  const paginateCollaborators = (data: any[] = []) => {
    const from = actualPage * paginatorState;
    const to = from + paginatorState;
    const pageData = data.slice(from, to);
    setCurrentPageData(pageData || []);
    setLoading(false);
  };

  const onCheckAll = useCallback(() => {
    if (allSelected) {
      setSelectedMap({});
    } else {
      const newMap = collaboratorsToAdd?.reduce((map: ISelectedMap, obj) => {
        map[obj.email] = true;
        return map;
      }, {});
      setSelectedMap(newMap ?? {});
    }
    setAllSelected(!allSelected);
  }, [allSelected, collaboratorsToAdd]);

  const onCheckRow = useCallback((key: string) => {
    setSelectedMap((prevMap) => {
      const newMap = { ...prevMap };
      if (newMap[key]) {
        delete newMap[key];
      } else {
        newMap[key] = true;
      }
      return newMap;
    });
  }, []);

  const closeActionBar = useCallback(() => {
    setSelectedMap({});
    setAllSelected(false);
    setTotalSelected(0);
  }, []);

  const sortData = (columnKey: string, isAscending: boolean) => {
    if (collaboratorsToAdd) {
      const sortedData = sortArray(collaboratorsToAdd, columnKey, isAscending);
      paginateCollaborators(sortedData);
    }
  };
  const onDeltePendingMembersToLocalTable = useCallback(
    async (member?: string[]) => {
      let collabs: string | string[] = [];
      setLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 250));
        if (member && member.length >= 1) {
          collabs = member;
        } else {
          collabs = Object.keys(selectedMap).filter((key) => selectedMap[key]);
        }
        setCollaboratorsToAdd((prev) =>
          prev ? prev.filter((collaborator) => !collabs.includes(collaborator.email)) : []
        );
        setAllSelected(false);
      } catch {
        setError(true);
      } finally {
        setTotalSelected(0);
        setSelectedMap({});
        setLoading(false);
      }
    },
    [selectedMap]
  );

  useEffect(() => {
    setActualPage(0);
  }, [collaboratorsToAdd, paginatorState]);

  useEffect(() => {
    if (collaboratorsToAdd) {
      paginateCollaborators(collaboratorsToAdd);
    } else {
      setCurrentPageData([]);
    }
  }, [collaboratorsToAdd, actualPage, paginatorState]);

  useEffect(() => {
    const selected = Object.values(selectedMap).filter(Boolean).length;
    setTotalSelected(selected);
    setAllSelected(selected === collaboratorsToAdd?.length && selected > 0);
  }, [selectedMap, collaboratorsToAdd]);

  useEffect(() => {
    if (isMounted) {
      setSelectedMap({});
      setAllSelected(false);
      onSearch();
    }
  }, [holding, isMounted]);

  const renderEmptyState = () => {
    if (error) {
      return (
        <div className="empty-collaborators-to-add">
          <ErrorCard
            primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
            secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
            action={() => onSearch()}
            buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
          />
        </div>
      );
    }

    if (emptySearch) {
      return (
        <div className="empty-collaborators-to-add">
          <EmptyCard
            center
            primaryText={t(TRANSLATION_COLLABORATORS.table.emptyData.toAddTable.title)}
            secondaryText={t(TRANSLATION_COLLABORATORS.table.emptyData.toAddTable.description, {
              hr_system: integrationName
            })}
          />
        </div>
      );
    }
  };

  return (
    <div className="collaborators-to-add-home">
      {collaboratorsToAdd && (integrationName || !isEmptyObject(lastSyncAt)) && (
        <div className="collaborators-to-add-banner">
          {integrationName && (
            <div className="collaborators-to-add-banner-hr-name">
              <Trans
                i18nKey={TRANSLATION_COLLABORATORS.table.toAddInfoBanner.body}
                values={{ hr_system: integrationName }}
                components={{
                  b: <b></b>
                }}
              />
            </div>
          )}
          {!isEmptyObject(lastSyncAt) && (
            <div className="collaborators-to-add-banner-last-sync">
              {t(TRANSLATION_COLLABORATORS.table.toAddInfoBanner.date, {
                sync_date: lastSyncAt[0],
                sync_hour: lastSyncAt[1]
              })}
            </div>
          )}
        </div>
      )}
      <TableFilter
        onSearch={onSearch}
        loading={loading}
        searchBarPlaceholder={TRANSLATION_COLLABORATORS.table.searchBarPlaceholderUnsubscriptions}
      />
      {renderEmptyState() || (
        <StyledTable
          data={currentPageData}
          sortData={sortData}
          loading={loading}
          columns={COLLABORATORS_TO_ADD_TABLE_COLUMNS}
          onChange={onDeltePendingMembersToLocalTable}
          dataKey="email"
          checkProps={{ onCheckRow, onCheckAll, checkedRows: selectedMap, allSelected }}
        />
      )}
      {collaboratorsToAdd && (
        <div className="paginator-container">
          <TablePaginationV3
            rowsPerPageOptions={DEFAULT_PAGINATOR_OPTIONS_V3}
            rowsPerPage={paginatorState}
            count={getTotalCollaboratorsToAdd()}
            actualPage={actualPage}
            onPageChange={setActualPage}
            onRowsPerPageChange={setPaginatorState}
            sectionPage={SECTION_COLLABORATORS}
            marginTop={20}
          />
        </div>
      )}
      <div className="modal">
        <CollaboratorsAssignModal
          emails={Object.keys(selectedMap)}
          openModal={addCollaboratorModal}
          onFinally={onDeltePendingMembersToLocalTable}
          setOpenModal={setAddCollaboratorModal}
        />
        <CollaboratorsDismissModal
          emails={Object.keys(selectedMap)}
          openModal={deleteCollaboratorModal}
          onFinally={onDeltePendingMembersToLocalTable}
          setOpenModal={setDeleteCollaboratorModal}
        />
      </div>
      {selectedMap && !isEmptyObject(selectedMap) && (
        <div className="collaborators-action-bar">
          <ActionBar
            text={actionBarText}
            onPressAdd={() => setAddCollaboratorModal(true)}
            onPressIgnore={() => setDeleteCollaboratorModal(true)}
            onPressX={closeActionBar}
          />
        </div>
      )}
    </div>
  );
};

export default CollaboratorToAddTable;
